if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.swiper = null;
        this.thumbnailClickBound = false;
      }

      connectedCallback() {
        this.refreshElements();
        this.initSwiper();
        this.bindThumbnailClicks();

        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      refreshElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      getSafeSelector(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') {
          return CSS.escape(value);
        }

        return String(value).replace(/["\\]/g, '\\$&');
      }

      initSwiper() {
        this.refreshElements();

        const viewer = this.querySelector('.product-main-swiper');
        if (!viewer) return;
        if (typeof Swiper === 'undefined') return;

        const wrapper = viewer.querySelector('.swiper-wrapper');
        const slides = viewer.querySelectorAll('.swiper-slide');

        if (!wrapper || !slides.length) return;

        if (this.swiper && typeof this.swiper.destroy === 'function') {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }

        const nextEl = viewer.querySelector('.product-swiper-next');
        const prevEl = viewer.querySelector('.product-swiper-prev');
        const paginationEl = viewer.querySelector('.swiper-pagination');

        this.swiper = new Swiper(viewer, {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: false,
          autoHeight: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
          resizeObserver: true,

          navigation: nextEl && prevEl
            ? {
                nextEl: nextEl,
                prevEl: prevEl,
              }
            : false,

          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : false,

          on: {
            init: () => {
              this.syncActiveFromSwiper();
            },
            slideChange: () => {
              this.onSwiperSlideChanged();
            },
          },
        });

        this.syncActiveFromSwiper();
      }

      refreshSwiper() {
        if (!this.swiper) {
          this.initSwiper();
          return;
        }

        if (!this.swiper.el || !document.body.contains(this.swiper.el)) {
          this.swiper = null;
          this.initSwiper();
          return;
        }

        this.swiper.update();
        this.swiper.updateSlides();
        this.swiper.updateProgress();
        this.swiper.updateSlidesClasses();
      }

      bindThumbnailClicks() {
        if (this.thumbnailClickBound) return;
        this.thumbnailClickBound = true;

        this.addEventListener('click', (event) => {
          const thumbnailItem = event.target.closest('[data-target]');
          if (!thumbnailItem || !this.contains(thumbnailItem)) return;

          const thumbnailsWrapper = thumbnailItem.closest('[id^="GalleryThumbnails"]');
          if (!thumbnailsWrapper) return;

          event.preventDefault();

          const mediaId = thumbnailItem.dataset.target;
          if (!mediaId) return;

          this.setActiveMedia(mediaId, false);
        });
      }

      getSlideByMediaId(mediaId) {
        if (!this.elements.viewer || !mediaId) return null;

        const safeMediaId = this.getSafeSelector(mediaId);
        return this.elements.viewer.querySelector(`[data-media-id="${safeMediaId}"]`);
      }

      getThumbnailByMediaId(mediaId) {
        if (!this.elements.thumbnails || !mediaId) return null;

        const safeMediaId = this.getSafeSelector(mediaId);
        return this.elements.thumbnails.querySelector(`[data-target="${safeMediaId}"]`);
      }

      getSlideIndex(activeMedia) {
        if (!this.elements.viewer || !activeMedia) return -1;

        const slides = Array.from(this.elements.viewer.querySelectorAll('.swiper-slide[data-media-id]'));
        return slides.findIndex((slide) => slide.dataset.mediaId === activeMedia.dataset.mediaId);
      }

      setActiveMedia(mediaId, prepend = false) {
        this.refreshElements();

        const activeMedia = this.getSlideByMediaId(mediaId);
        if (!activeMedia) return;

        if (prepend) {
          const wrapper = activeMedia.parentElement;

          if (wrapper && wrapper.firstElementChild !== activeMedia) {
            wrapper.prepend(activeMedia);
          }

          const activeThumbnail = this.getThumbnailByMediaId(mediaId);
          const thumbWrapper = activeThumbnail?.parentElement;

          if (activeThumbnail && thumbWrapper && thumbWrapper.firstElementChild !== activeThumbnail) {
            thumbWrapper.prepend(activeThumbnail);
          }
        }

        this.refreshSwiper();

        const slideIndex = this.getSlideIndex(activeMedia);

        if (this.swiper && slideIndex > -1) {
          this.swiper.slideTo(slideIndex, 0, false);
          this.swiper.updateSlidesClasses();
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        this.preventStickyHeader();
        this.playActiveMedia(activeMedia);

        const activeThumbnail = this.getThumbnailByMediaId(activeMedia.dataset.mediaId);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail?.dataset.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      onSwiperSlideChanged() {
        this.syncActiveFromSwiper();
      }

      syncActiveFromSwiper() {
        if (!this.swiper) return;

        this.refreshElements();

        const activeSlide = this.swiper.slides[this.swiper.activeIndex];
        if (!activeSlide) return;

        const mediaId = activeSlide.dataset.mediaId;
        if (!mediaId) return;

        this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeSlide.classList.add('is-active');

        const thumbnail = this.getThumbnailByMediaId(mediaId);
        this.setActiveThumbnail(thumbnail);
        this.playActiveMedia(activeSlide);

        if (this.elements.liveRegion && thumbnail?.dataset.mediaPosition) {
          this.announceLiveRegion(activeSlide, thumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
          element.removeAttribute('aria-current');
        });

        const button = thumbnail.querySelector('button');
        if (button) button.setAttribute('aria-current', 'true');

        const thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');

        if (thumbnailList) {
          const thumbnailLeft = thumbnail.offsetLeft;
          const thumbnailWidth = thumbnail.offsetWidth;
          const listWidth = thumbnailList.clientWidth;

          const scrollLeft = thumbnailLeft - listWidth / 2 + thumbnailWidth / 2;

          thumbnailList.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion || !window.accessibilityStrings?.imageAvailable) return;

        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };

        if (image.complete) {
          announce();
        } else {
          image.onload = announce;
        }
      }

      playActiveMedia(activeItem) {
        if (typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        const slides = this.elements.viewer?.querySelectorAll('.swiper-slide');
        if (!slides) return;

        this.elements.viewer.setAttribute('role', 'presentation');
        slides.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}